// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { array, bool, func, string, object } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Filter, ArrowDownWideNarrow, RefreshCw, Info, X } from 'lucide-react';
import {
  DatePicker,
  DatePickerInput,
  TextInput,
  Button,
  RadioButtonGroup,
  RadioButton,
} from '@carbon/react';

import { NWComboBox, NWNotification } from 'Components/shared';

import IconInvoice from 'Assets/images/pdf.svg';
import IconCsv from 'Assets/images/csv.svg';

const StyledFilterOrder = styled.div`
  .mobileToolbar {
    display: none;
    gap: 1em;
    margin-bottom: 18px;

    &.fixed {
      display: none;
      justify-content: center;
      align-items: center;
      padding: 15px;
      margin: 0;
      width: 100vw;
      height: 80px;
      position: fixed;
      bottom: 0;
      left: 0;
      transition: transform 0.2s ease;
      overflow: visible;
      opacity: ${ props => props.isVisible ? 1 : 0 };
      visibility: ${ props => props.isVisible ? 'visible' : 'hidden' };
      background-color: ${ props => props.theme.colors.white };
      box-shadow: 0 3px 6px #00000029;
      z-index: 8999;
    }

    button {
      display: flex;
      flex: 1;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      padding: 8px;
      background: #E7ECF2;
      border: 1px solid #B2C2D5;
      border-radius: 10px;
      cursor: pointer;

      .title {
        display: flex;
        align-items: center;
        font-size: 16px;
        line-height: 24px;
        font-weight: 600;

        svg {
          margin-right: 8px;
        }
      }

      .info {
        color: #707070;
        font-size: 12px;
        line-height: 16px;
      }
    }
  }

  .backDrop {
    position: fixed;
    inset: 0;
    z-index: 10;
    background: #000000b3;
    display: ${ props => props.mobileSortOpen ? 'flex' : 'none' };
    align-items: flex-end;

    .content {
      flex: 1;
      padding: 15px;
      background: #fff;

      .header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;

        h3 {
          font-size: 20px;
          line-height: 27px;
        }

        button {
          background: transparent;
          border: none;
          cursor: pointer;
        }
      }

      .cds--radio-button-group {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        .cds--radio-button-wrapper {
          margin: 12px 0;
        }
      }
    }
  }

  .filterSection {
    display: flex;
    flex-direction: column;
    padding: 25px 0;
    gap: 20px;

    .cds--date-picker, .cds--date-picker-container {
      width: 100%;

      .cds--date-picker-input__wrapper span {
        width: 100%;
      }
    }

    .mobileHeader {
      display: none;

      .mobileTitleSection {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 25px 15px 15px;

        h2 {
          font-size: 20px;
        }

        button {
          cursor: pointer;
          color: #D52B1E;
          background: transparent;
          border: none;
        }
      }

      .activeFilters {
        display: flex;
        background: #F3F2F2;
        padding: 15px;
        flex-direction: column;

        > span {
          font-weight: 600;
        }

        .activeFilterList {
          padding: 10px 0;
          display: flex;
          text-wrap: nowrap;
          overflow-x: scroll;

          .filter { 
            margin-right: 10px;
          }
        }

        button {
          cursor: pointer;
          background: transparent;
          border: none;

          svg {
            fill: #D52B1E;
          }
        }
      }
    }

    .filters {
      display: flex;

      > div {
        padding: 0 5px;
        flex: 0 1 auto;
        min-width: 25%;

        .cds--combo-box {
          margin-bottom: 0;
        }

        .cds--date-picker.cds--date-picker--single .cds--date-picker__input {
          width: 100%;
        }

        &:first-of-type {
          padding-left: 0;
        }

        &:last-of-type {
          padding-right: 0;
        }

        &:nth-of-type(6) {
          flex: 1;
          min-width: 37.5%;
        }
      }
    }

    .refreshActions {
      display: flex;
      justify-content: end;
      align-items: center;
      gap: 15px;

      .refreshInvoices {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
        font-size: 16px;

        &:disabled {
          cursor: not-allowed;
          color: #707070;
          fill: #707070;
        }

        &.rotate svg {
          animation: rotate 2s linear infinite;
        }
      }

      .downloadInvoices {
        display: flex;
        align-items: center;

        span {
          margin-right: 12px;
        }

        svg {
          width: 24px;
          height: 30px;
        }
      }

      button {
        background: transparent;
        border: none;
        cursor: pointer;
      }
    }

    .actionsSection {
      display: flex;
      justify-content: space-between;
      align-items: start;
      gap: 10px;

      .cds--form-item, .state {
        width: 100%;
        max-width: calc(25% - 5px);

        &.iban {
          max-width: calc(50% - 5px);
        }
      }

      .actions {
        display: flex;
        gap: 10px;
        width: calc(50% - 5px);
        justify-content: end;
      
        &.grow {
          flex: 1;
        }

        button.cds--btn {
          height: 40px;
          max-width: none;
          min-width: 170px;
          -webkit-box-pack: center;
          justify-content: center;
          font-size: 1rem;
          text-align: center;

          &.deleteFilters {
            border: 1px solid #D52B1E;
            color: #D52B1E;

            &:hover {
              background: transparent;
            }
          }
        }
      }
    }

    .cds--label {
      display: none;
    }

    .cds--select-input, .cds--date-picker__input, .cds--text-input {
      background: #fff;
      border: 1px solid #e2e2e2;
      border-radius: 5px;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .filterSection {
      display: ${ props => props.mobileFiltersOpen ? 'flex' : 'none' };
      position: fixed;
      inset: 0;
      background: #fff;
      z-index: 9001;
      margin-top: 0;
      overflow-y: scroll;

      .cds--label {
        display: inline-block;
        font-size: 15px;
        font-weight: 600;
        color: #3a3a3a;
      }

      .mobileHeader {
        display: block;
      }

      .refreshActions {
        justify-content: space-between;
        padding: 0 15px;
  
        .downloadInvoices {
          span {
            display: none;
          }
        }
      }

      .actionsSection {
        > div:not(.actions) {
          width: 100%;
          min-width: 100%;
        }
      }

      .filters {
        gap: 20px;
        flex-direction: column;
        padding: 15px;

        .cds--date-picker {
          width: 100%;

          .cds--date-picker-container {
            width: 100%;
          }
        }
      }

      .actionsSection {
        gap: 20px;
        flex-direction: column;
        padding: 15px;

        .actions {
          width: 100%;

          button.cds--btn {
            min-width: 100%;

            &.deleteFilters {
              display: none;
            }
          }
        }
      }
    }

    .mobileToolbar {
      display: flex;

      &.fixed {
        display: flex;
      }
    }
  }
`;

const FilterReports = ({
  register,
  control,
  getValues,
  setValue,
  onSubmit,
  onClearFilters,
  onClearFilter,
  onChangeDate,
  states,
  startDate,
  endDate,
  documentStartDate,
  documentEndDate,
  dueDateStart,
  dueDateEnd,
  paymentsStart,
  paymentsEnd,
  setMobileFiltersOpen,
  mobileFiltersOpen,
  setMobileSortOpen,
  handleMobileOrderChange,
  activeFilters,
  currentSort,
  sortOptions,
  mobileSortOpen,
  onSyncInvoices,
  onDownload,
  syncLoading,
  timeToDisplay,
  errors,
  locationPathname,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const filterKeys = useMemo(() => [
    { key: 'invoice_number', label: t('orders.invoiceNumber') },
    { key: 'purchase_order', label: t('orders.clientOrderNumber') },
    { key: 'product_code', label: t('orders.searchProduct') },
    { key: 'status', label: t('orders.state') },
    { key: 'date_start', label: t('orders.dateFrom') },
    { key: 'date_end', label: t('orders.dateTo') },
    { key: 'due_date_start', label: t('orders.dueDateFrom') },
    { key: 'due_date_end', label: t('orders.dueDateTo') },
    { key: 'invoice_date_start', label: t('orders.createdFrom') },
    { key: 'invoice_date_end', label: t('orders.createdUpTo') },
    { key: 'document_date_start', label: t('orders.createdFrom') },
    { key: 'document_date_end', label: t('orders.createdUpTo') },
    { key: 'value_from', label: t('orders.valueFrom') },
    { key: 'value_to', label: t('orders.valueTo') },
    { key: 'iban', label: t('orders.iban') },
  ], []);

  const renderFilter = ({ key, value }) => {
    const filterName = filterKeys.find(item => item.key === key)?.label;
    let filterValue = value;

    if (key === 'status') {
      filterValue = states.find(item => item.value === value)?.label;
    }

    return (
      <div>
        <span>{ `${ filterName }: ${ filterValue }` }</span>
        <button type="button" onClick={ () => onClearFilter(key) }><X /></button>
      </div>
    );
  };

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    ...register(fieldName),
  });

  const sharedComboBoxProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    control,
  });

  return (
    <StyledFilterOrder theme={ theme } mobileSortOpen={ mobileSortOpen } mobileFiltersOpen={ mobileFiltersOpen }>
      <div className="mobileToolbar">
        <button type="button" onClick={ () => setMobileFiltersOpen(true) }>
          <span className="title">
            <Filter />
            { t('filters.filter') }
          </span>
          <span className="info">{ `${ Object.keys(activeFilters || {}).length } ${t('filters.selectedFilters')}`} </span>
        </button>
        <button type="button" onClick={ () => setMobileSortOpen(true) }>
          <span className="title">
            <ArrowDownWideNarrow />
            { t('products.sort') }
          </span>
          <span className="info">{ currentSort?.label }</span>
        </button>
      </div>
      <div className="mobileSort">
        <div className="backDrop">
          <div className="content">
            <div className="header">
              <h3>{ t('products.sortBy') }</h3>
              <button type="button" onClick={ () => setMobileSortOpen(false) }>
                <X />
              </button>
            </div>
            <RadioButtonGroup
              name="mobileOrderChange"
              valueSelected={ currentSort?.id }
              onChange={ handleMobileOrderChange }
            >
              { sortOptions.map(opt => (
                <RadioButton key={ opt.id } labelText={ opt.label } value={ opt.id } id={ opt.id } />
              )) }
            </RadioButtonGroup>
          </div>
        </div>
      </div>
      <div className="filterSection">
        <div className="mobileHeader">
          <div className="mobileTitleSection">
            <h2>{ t('orders.filters') }</h2>
            <button
              type="button"
              onClick={ () => {
                onClearFilters();
                setMobileFiltersOpen(false);
              } }
            >
              { t('products.clearAllFilters')}
            </button>
          </div>
          { Object.keys(activeFilters).length > 0 && (
            <div className="activeFilters">
              <span>{ `${ Object.keys(activeFilters).length } ${ t('orders.activeFilters') }` }</span>
              <div className="activeFilterList">
                { Object.entries(activeFilters).map(([key, value]) => <div key={ key } className="filter">{ renderFilter({ key, value }) }</div>) }
              </div>
            </div>
          )}
        </div>
        <NWNotification errors={ errors } delay={ 10 } />
        <div className="refreshActions">
          { locationPathname !== '/reports/payments' && (
            <button
              className={ `refreshInvoices ${ syncLoading ? 'rotate' : ''}` }
              type="button"
              disabled={ timeToDisplay !== '00:00' }
              onClick={ onSyncInvoices }
            >
              <RefreshCw size={20} />
              { t('orders.refreshInvoices') }
              { timeToDisplay !== '00:00' && (
                <div className="refreshTooltip">
                  <span id="refreshTooltip"><Info size={ 20 } /></span>
                  <ReactTooltip
                    anchorId="refreshTooltip"
                    variant="light"
                    offset={ 10 }
                    content={ (
                      <p>{ t('orders.refreshInvoicesInfo', { time: timeToDisplay }) }</p>
                    ) }
                  />
                </div>
              ) }
            </button>
          )}
          <div className="downloadInvoices">
            <span>{ locationPathname === '/reports/payments' ? t('orders.downloadPayments') : t('orders.downloadInvoices') }:</span>
            <button type="button" onClick={ () => onDownload('pdf') }>
              <IconInvoice />
            </button>
            <button type="button" onClick={ () => onDownload('csv') }>
              <IconCsv />
            </button>
          </div>
        </div>
        <div className="filters">
          { (locationPathname === '/reports/invoices' || locationPathname === '/reports/unpaid-invoices') && (
            <>
              <TextInput
                id="search"
                autoComplete="none"
                labelText={ t('orders.invoiceNumber') }
                placeholder={ t('orders.invoiceNumber') }
                size="md"
                { ...sharedProps('invoice_number') }
              />
              <TextInput
                id="search"
                autoComplete="none"
                labelText={ t('orders.clientOrderNumber') }
                placeholder={ t('orders.clientOrderNumber') }
                size="md"
                { ...sharedProps('purchase_order') }
              />
            </>
          )}
          { (
            locationPathname === '/reports/invoices'
            || locationPathname === '/reports/unpaid-invoices'
          ) && (
            <>
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                value={ startDate }
                maxDate={ endDate }
                onChange={ date => onChangeDate('invoice_date_start', date) }
              >
                <DatePickerInput
                  autoComplete="none"
                  placeholder={ t('orders.createdFrom') }
                  labelText={ t('orders.createdFrom') }
                  id="invoice-date-start"
                  size="md"
                />
              </DatePicker>
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                value={ endDate }
                minDate={ startDate }
                onChange={ date => onChangeDate('invoice_date_end', date) }
              >
                <DatePickerInput
                  autoComplete="none"
                  placeholder={ t('orders.createdUpTo') }
                  labelText={ t('orders.createdUpTo') }
                  id="invoice-date-end"
                  size="md"
                />
              </DatePicker>
            </>
          )}
          { locationPathname === '/reports/balance' && (
            <>
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                value={ documentStartDate }
                maxDate={ documentEndDate }
                onChange={ date => onChangeDate('document_date_start', date) }
              >
                <DatePickerInput
                  autoComplete="none"
                  placeholder={ t('orders.createdFrom') }
                  labelText={ t('orders.createdFrom') }
                  id="document-date-start"
                  size="md"
                />
              </DatePicker>
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                value={ documentEndDate }
                minDate={ documentStartDate }
                onChange={ date => onChangeDate('document_date_end', date) }
              >
                <DatePickerInput
                  autoComplete="none"
                  placeholder={ t('orders.createdUpTo') }
                  labelText={ t('orders.createdUpTo') }
                  id="document-date-end"
                  size="md"
                />
              </DatePicker>
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                value={ dueDateStart }
                maxDate={ dueDateEnd }
                onChange={ date => onChangeDate('due_date_start', date) }
              >
                <DatePickerInput
                  autoComplete="none"
                  placeholder={ t('orders.dueDateFrom') }
                  labelText={ t('orders.dueDateFrom') }
                  id="due-date-start"
                  size="md"
                />
              </DatePicker>
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                value={ dueDateEnd }
                minDate={ dueDateStart }
                onChange={ date => onChangeDate('due_date_end', date) }
              >
                <DatePickerInput
                  autoComplete="none"
                  placeholder={ t('orders.dueDateTo') }
                  labelText={ t('orders.dueDateTo') }
                  id="due-date-end"
                  size="md"
                />
              </DatePicker>
            </>
          )}
          { locationPathname === '/reports/payments' && (
            <>
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                value={ paymentsStart }
                maxDate={ paymentsEnd }
                onChange={ date => onChangeDate('date_start', date) }
              >
                <DatePickerInput
                  autoComplete="none"
                  placeholder={ t('orders.dateFrom') }
                  labelText={ t('orders.dateFrom') }
                  id="date-start"
                  size="md"
                />
              </DatePicker>
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                value={ paymentsEnd }
                minDate={ paymentsStart }
                onChange={ date => onChangeDate('date_end', date) }
              >
                <DatePickerInput
                  autoComplete="none"
                  placeholder={ t('orders.dateTo') }
                  labelText={ t('orders.dateTo') }
                  id="date-end"
                  size="md"
                />
              </DatePicker>
              <TextInput
                id="value_from"
                autoComplete="none"
                labelText={ t('orders.valueFrom') }
                placeholder={ t('orders.valueFrom') }
                size="md"
                { ...sharedProps('value_from') }
              />
              <TextInput
                id="value_to"
                autoComplete="none"
                labelText={ t('orders.valueTo') }
                placeholder={ t('orders.valueTo') }
                size="md"
                { ...sharedProps('value_to') }
              />
            </>
          )}
        </div>
        <div className="actionsSection">
          { (locationPathname === '/reports/invoices' || locationPathname === '/reports/unpaid-invoices') && (
            <TextInput
              id="product"
              autoComplete="none"
              className="product"
              labelText={ t('orders.searchProduct') }
              placeholder={ t('orders.searchProduct') }
              size="md"
              { ...sharedProps('product_code') }
            />
          ) }
          { locationPathname === '/reports/invoices' && (
            <NWComboBox
              key={ getValues('status') }
              titleText={ t('orders.state') }
              autoComplete="none"
              className="state"
              placeholder={ t('orders.state') }
              light
              { ...sharedComboBoxProps('status') }
              items={ states }
              onChange={ value => setValue('status', value?.selectedItem?.value) }
              initialSelectedItem={ states.find(item => item.value === getValues('status')) || '' }
            />
          )}
          { locationPathname === '/reports/payments' && (
            <TextInput
              id="iban"
              autoComplete="none"
              className="iban"
              labelText={ t('orders.iban') }
              placeholder={ t('orders.iban') }
              size="md"
              { ...sharedProps('iban') }
            />
          ) }
          <div className={ `actions ${ locationPathname === '/reports/payments' || locationPathname === '/reports/balance' ? 'grow' : '' }` }>
            <Button
              className="FilterReports"
              kind="danger"
              onClick={ onSubmit }
            >
              { t('orders.filter') }
            </Button>
            { !!Object.keys(activeFilters).length && (
              <Button
                className="deleteFilters"
                kind="danger--ghost"
                onClick={ onClearFilters }
              >
                { t('order.deleteFilters') }
              </Button>
            )}
          </div>
        </div>
      </div>
    </StyledFilterOrder>
  );
};

FilterReports.propTypes = {
  register: func,
  control: object,
  getValues: func,
  setValue: func,
  onSubmit: func,
  onClearFilters: func,
  onClearFilter: func,
  onChangeDate: func,
  states: array,
  startDate: string,
  endDate: string,
  documentStartDate: string,
  documentEndDate: string,
  dueDateStart: string,
  dueDateEnd: string,
  paymentsStart: string,
  paymentsEnd: string,
  setMobileFiltersOpen: func,
  setMobileSortOpen: func,
  handleMobileOrderChange: func,
  activeFilters: object,
  currentSort: object,
  mobileFiltersOpen: bool,
  sortOptions: array,
  mobileSortOpen: bool,
  onSyncInvoices: func,
  onDownload: func,
  syncLoading: bool,
  timeToDisplay: string,
  errors: array,
  locationPathname: string,
};

FilterReports.displayName = 'FilterReportsComponent';

export default FilterReports;
